import { formatNumber } from '@/utils/numberUtils';

import { toNumberOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { isAllQuarters } from '../../../utils/partnerScoreUtils';
import { UseTotalNetPromoterScoreResponse } from './types';

const getPercentage = (value = 0, total = 1) => (total > 0 ? (value / total) * 100 : 0);

type GetSegmentResponse = Pick<
  UseTotalNetPromoterScoreResponse,
  'npsGreenCount' | 'npsOrangeCount' | 'npsRedCount' | 'total'
>;

export function getSegment(data: CubeRawRow[], cubeName: string): GetSegmentResponse {
  const responseKey = `${cubeName}.response_nps_q1`;
  const countKey = `${cubeName}.countOfSurveys`;
  const segmentCounts = data.reduce<GetSegmentResponse>(
    (acc, record) => {
      const responseValue = toNumberOrNull(record[responseKey]);
      if (responseValue === null) return acc;

      const count = toNumberOrNull(record[countKey]) ?? 0;
      acc.total += count;

      if (responseValue >= 0 && responseValue <= 6) acc.npsRedCount += count;
      else if (responseValue >= 7 && responseValue <= 8) acc.npsOrangeCount += count;
      else if (responseValue >= 9 && responseValue <= 10) acc.npsGreenCount += count;

      return acc;
    },
    { npsRedCount: 0, npsOrangeCount: 0, npsGreenCount: 0, total: 0 },
  );

  const netPromoterScore = {
    npsRedCount: segmentCounts.npsRedCount,
    npsOrangeCount: segmentCounts.npsOrangeCount,
    npsGreenCount: segmentCounts.npsGreenCount,
    total: segmentCounts.total,
  };

  return netPromoterScore;
}

type CalculateAverageNpsPercentageParams = Pick<
  UseTotalNetPromoterScoreResponse,
  'npsGreenCount' | 'npsOrangeCount' | 'npsRedCount' | 'total'
>;

export function calculateAverageNpsPercentage(averageNpsScore: CalculateAverageNpsPercentageParams) {
  const red = parseFloat(formatNumber(getPercentage(averageNpsScore?.npsRedCount, averageNpsScore?.total), 1));
  const orange = parseFloat(formatNumber(getPercentage(averageNpsScore?.npsOrangeCount, averageNpsScore?.total), 1));
  const green = parseFloat(formatNumber(getPercentage(averageNpsScore?.npsGreenCount, averageNpsScore?.total), 1));

  return {
    red,
    orange,
    green,
  };
}

type CalculateNetChangeParams = Pick<UseTotalNetPromoterScoreResponse['percentage'], 'green' | 'red'>;

export function calculateNetChange(percentage: CalculateNetChangeParams): number {
  const npsValue = percentage?.green - percentage?.red;
  return Math.round(npsValue * 10) / 10;
}

export function formatNetChangeAsString(change: number): string {
  const formattedValue = change.toFixed(1);
  return change > 0 ? `+${formattedValue}` : formattedValue;
}

export const calculateNpsScoreForPeriod = ({
  data,
  quarter,
  year,
  cubeName,
}: {
  data: CubeRawRow[];
  year: number;
  quarter: number;
  cubeName: string;
}) => {
  const yearKey = `${cubeName}.year`;
  const quarterKey = `${cubeName}.quarter`;
  const filteredData = data.filter(dataRow => {
    const rowYear = dataRow[yearKey];
    const rowQuarter = dataRow[quarterKey];

    if (isAllQuarters(quarter)) {
      return rowYear === year;
    }
    return rowYear === year && rowQuarter === quarter;
  });

  const netPromoterScore = getSegment(filteredData, cubeName);
  const percentage = calculateAverageNpsPercentage(netPromoterScore);
  const percentageChange = calculateNetChange(percentage);
  const percentageChangeString = formatNetChangeAsString(percentageChange);

  const result: Omit<UseTotalNetPromoterScoreResponse, 'changeValue'> = {
    ...netPromoterScore,
    percentage,
    percentageChange,
    percentageChangeString,
  };

  return result;
};

/** Calculates NPS score from pre-grouped rows (O(1) lookup). Use when rows are already filtered by period. */
export function calculateNpsScoreFromRows(
  rows: CubeRawRow[],
  cubeName: string,
): Omit<UseTotalNetPromoterScoreResponse, 'changeValue'> {
  const netPromoterScore = getSegment(rows, cubeName);
  const percentage = calculateAverageNpsPercentage(netPromoterScore);
  const percentageChange = calculateNetChange(percentage);
  const percentageChangeString = formatNetChangeAsString(percentageChange);

  return {
    ...netPromoterScore,
    percentage,
    percentageChange,
    percentageChangeString,
  };
}
